"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.canFormatType = canFormatType;
exports.getFormatOptions = getFormatOptions;
exports.getDefaultFormat = getDefaultFormat;
exports.formatValue = formatValue;
const d3_format_1 = require("d3-format");
const utm_1 = require("utm");
const dayjs_1 = __importDefault(require("./dayjs"));
const wellknown_1 = require("./wellknown");
/** Determine if can format type */
function canFormatType(type) {
    return type == "number" || type == "geometry" || type === "date" || type === "datetime";
}
/** Get available options for formatting a type. Null if not available */
function getFormatOptions(type) {
    if (type == "number") {
        return [
            { value: "", label: T `Plain: 1234.567` },
            { value: ",", label: T `Normal: 1,234.567` },
            { value: ",.0f", label: T `Rounded: 1,234` },
            { value: ",.2f", label: T `Two decimals: 1,234.56` },
            { value: "$,.2f", label: T `Currency: $1,234.56` },
            { value: "$,.0f", label: T `Currency rounded: $1,234` },
            { value: ".0%", label: T `Percent rounded: 12%` },
            { value: ".2%", label: T `Percent decimal: 12.34%` }
        ];
    }
    if (type == "geometry") {
        return [
            { value: "lat, lng", label: T `Latitude, Longitude` },
            { value: "UTM", label: T `UTM` },
            { value: "WKT", label: T `Well-Known Text` }
        ];
    }
    if (type == "date") {
        return [
            { value: "ll", label: (0, dayjs_1.default)().format("ll") },
            { value: "YYYY-MM-DD", label: (0, dayjs_1.default)().format("YYYY-MM-DD") },
            { value: "YYYY-MMM-DD", label: (0, dayjs_1.default)().format("YYYY-MMM-DD") },
            { value: "YYYY-MM", label: (0, dayjs_1.default)().format("YYYY-MM") },
            { value: "YYYY", label: (0, dayjs_1.default)().format("YYYY") },
            { value: "MMM YYYY", label: (0, dayjs_1.default)().format("MMM YYYY") },
            { value: "MMM DD, YYYY", label: (0, dayjs_1.default)().format("MMM DD, YYYY") },
            { value: "MMMM D, YYYY", label: (0, dayjs_1.default)().format("MMMM D, YYYY") },
            { value: "MM/DD/YYYY", label: (0, dayjs_1.default)().format("MM/DD/YYYY") },
            { value: "DD/MM/YYYY", label: (0, dayjs_1.default)().format("DD/MM/YYYY") },
            { value: "DD-MM-YYYY", label: (0, dayjs_1.default)().format("DD-MM-YYYY") },
        ];
    }
    if (type == "datetime") {
        return [
            { value: "lll", label: (0, dayjs_1.default)().format("lll") },
            { value: "llll", label: (0, dayjs_1.default)().format("llll") },
            { value: "YYYY-MM-DD", label: (0, dayjs_1.default)().format("YYYY-MM-DD") },
            { value: "YYYY-MMM-DD", label: (0, dayjs_1.default)().format("YYYY-MMM-DD") },
            { value: "YYYY-MM", label: (0, dayjs_1.default)().format("YYYY-MM") },
            { value: "YYYY", label: (0, dayjs_1.default)().format("YYYY") },
            { value: "MMM YYYY", label: (0, dayjs_1.default)().format("MMM YYYY") },
            { value: "MMM DD, YYYY h:mm A", label: (0, dayjs_1.default)().format("MMM DD, YYYY h:mm A") },
            { value: "MMMM D, YYYY h:mm A", label: (0, dayjs_1.default)().format("MMMM D, YYYY h:mm A") },
            { value: "MMM DD, YYYY", label: (0, dayjs_1.default)().format("MMM DD, YYYY") },
            { value: "MMMM D, YYYY", label: (0, dayjs_1.default)().format("MMMM D, YYYY") },
            { value: "MM/DD/YYYY", label: (0, dayjs_1.default)().format("MM/DD/YYYY") },
            { value: "DD/MM/YYYY", label: (0, dayjs_1.default)().format("DD/MM/YYYY") },
            { value: "DD-MM-YYYY", label: (0, dayjs_1.default)().format("DD-MM-YYYY") },
            { value: "YYYY-MM-DD HH:mm:ss", label: (0, dayjs_1.default)().format("YYYY-MM-DD HH:mm:ss") },
            { value: "ISO 8601", label: (0, dayjs_1.default)().toISOString() },
        ];
    }
    return null;
}
/** Get default format */
function getDefaultFormat(type) {
    if (type == "number") {
        return ",";
    }
    if (type == "geometry") {
        return "lat, lng";
    }
    if (type == "date") {
        return "ll";
    }
    if (type == "datetime") {
        return "lll";
    }
    throw new Error(T `Not supported`);
}
/** Format a value of a specified type as a string. For historical reasons,
 * LayeredCharts multiply by 100 before adding the % sign. Set legacyPercentFormat to true to replicate
 */
function formatValue(type, value, format, locale, legacyPercentFormat) {
    if (value == null) {
        return "";
    }
    // Default
    format = format != null ? format : getDefaultFormat(type);
    // Use d3 format if number
    if (type == "number") {
        // Do not convert % (d3Format multiplies by 100 which is annoying)
        if (format.match(/%/) && !legacyPercentFormat) {
            value = value / 100.0;
        }
        return (0, d3_format_1.format)(format)(value);
    }
    if (["date", "datetime"].includes(type)) {
        if (format == "ISO 8601") {
            return (0, dayjs_1.default)(value).toISOString();
        }
        return (0, dayjs_1.default)(value).format(format);
    }
    if (type == "geometry") {
        if (format == "UTM") {
            if (value.type == "Point") {
                const latitude = value.coordinates[1];
                const longitude = value.coordinates[0];
                if (latitude > 84 || latitude < -80) {
                    return T `latitude out of range`;
                }
                if (longitude > 180 || longitude < -180) {
                    return T `longitude out of range`;
                }
                const { easting, northing, zoneNum, zoneLetter } = (0, utm_1.fromLatLon)(latitude, longitude);
                return `${zoneNum}${zoneLetter} ${easting.toFixed(0)} ${northing.toFixed(0)}`;
            }
            return value.type;
        }
        else if (format == "WKT") {
            return (0, wellknown_1.stringify)(value);
        }
        else {
            // Display as lat/lng if Point, otherwise type
            if (value.type == "Point") {
                return `${value.coordinates[1].toFixed(6)}, ${value.coordinates[0].toFixed(6)}`;
            }
            else {
                return value.type;
            }
        }
    }
    else {
        // Should not happen
        return value + "";
    }
}
